/**
 * Privacy Policy — Static legal page.
 * Route: /#/legal/privacy
 */
import LegalLayout, { legalH2, legalP, legalUl } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="May 16, 2026">
      <p style={legalP}>
        Mi6 Group (&ldquo;Mi6&rdquo;) operates the Attilius platform and the mi6group.com
        websites (the &ldquo;Service&rdquo;). This Privacy Policy explains what we collect,
        why we collect it, how we use it, and the choices available to you.
      </p>

      <h2 style={legalH2}>1. Information we collect</h2>
      <ul style={legalUl}>
        <li>
          <strong>Account information</strong> &mdash; name, work email, role, tenant assignment,
          and authentication credentials. Provided by you or your employer when
          creating an account.
        </li>
        <li>
          <strong>Customer Data</strong> &mdash; sales pipeline records, accounts, contacts,
          activities, and notes that you and your colleagues enter into the Service.
        </li>
        <li>
          <strong>Usage data</strong> &mdash; pages visited, features used, timestamps, IP
          address, browser type. Collected automatically to operate, secure, and
          improve the Service.
        </li>
        <li>
          <strong>Communications</strong> &mdash; email exchanges with our support team,
          feedback forms, and notification preferences.
        </li>
      </ul>

      <h2 style={legalH2}>2. How we use information</h2>
      <p style={legalP}>We use the information described above to:</p>
      <ul style={legalUl}>
        <li>Provide, maintain, and improve the Service.</li>
        <li>Authenticate users and enforce access controls.</li>
        <li>Send transactional emails such as password resets, support replies, and morning digests.</li>
        <li>Detect, investigate, and prevent security incidents and abuse.</li>
        <li>Comply with legal obligations and respond to lawful requests.</li>
      </ul>
      <p style={legalP}>
        We do not sell personal information. We do not use Customer Data to train
        third-party AI models without explicit written permission from your
        organization.
      </p>

      <h2 style={legalH2}>3. Legal bases (EEA / UK)</h2>
      <p style={legalP}>
        Where the GDPR applies, we rely on the following bases: performance of a
        contract (operating the Service for your employer), legitimate interests
        (securing and improving the Service), and consent (where required, for
        example for optional analytics).
      </p>

      <h2 style={legalH2}>4. Sub-processors and sharing</h2>
      <p style={legalP}>
        We engage trusted vendors to operate parts of the Service. Current
        sub-processors include:
      </p>
      <ul style={legalUl}>
        <li>Supabase &mdash; managed Postgres, authentication, and storage.</li>
        <li>Netlify &mdash; static hosting and edge functions.</li>
        <li>Resend &mdash; transactional email delivery.</li>
        <li>Microsoft 365 &mdash; fallback email delivery and internal communications.</li>
      </ul>
      <p style={legalP}>
        We require each sub-processor to provide appropriate security and to
        process personal data only on our documented instructions. We may also
        share information when required by law, to enforce our Terms, or to
        protect the safety of users.
      </p>

      <h2 style={legalH2}>5. Data retention</h2>
      <p style={legalP}>
        We retain Customer Data for as long as your organization&rsquo;s subscription is
        active. After termination we make data available for export for thirty
        (30) days, then delete or anonymize it within ninety (90) days unless a
        longer period is required by law. Backups are rotated on a thirty (30) day
        cycle.
      </p>

      <h2 style={legalH2}>6. Security</h2>
      <p style={legalP}>
        We use industry-standard safeguards including encryption in transit (TLS
        1.2 or higher), encryption at rest, role-based access control,
        tenant-level isolation in the database, and audit logging. No system is
        impenetrable, but we work continuously to maintain a strong security
        posture.
      </p>

      <h2 style={legalH2}>7. Your rights</h2>
      <p style={legalP}>
        Depending on your location, you may have rights to access, correct,
        delete, or port your personal information, and to object to or restrict
        certain processing. To exercise these rights contact your organization&rsquo;s
        administrator or email{" "}
        <a href="mailto:support_team@mi6group.com">support_team@mi6group.com</a>.
        We will respond within the timeframes required by applicable law.
      </p>

      <h2 style={legalH2}>8. International transfers</h2>
      <p style={legalP}>
        Mi6 is based in the United States and may process data in the United
        States and other countries. When personal information is transferred from
        the EEA, UK, or Switzerland, we rely on the European Commission&rsquo;s
        standard contractual clauses or another lawful transfer mechanism.
      </p>

      <h2 style={legalH2}>9. Children</h2>
      <p style={legalP}>
        The Service is not directed to children under 18 and we do not knowingly
        collect personal information from children. If we learn that we have
        collected personal information from a child we will delete it.
      </p>

      <h2 style={legalH2}>10. Changes to this Policy</h2>
      <p style={legalP}>
        We may update this Privacy Policy from time to time. Material changes
        will be communicated by email to the customer&rsquo;s administrator at least
        thirty (30) days before they take effect.
      </p>

      <h2 style={legalH2}>11. Contact</h2>
      <p style={legalP}>
        Questions about this Privacy Policy can be sent to{" "}
        <a href="mailto:support_team@mi6group.com">support_team@mi6group.com</a>.
      </p>
    </LegalLayout>
  );
}
